import { buildRendererPropsFromItem } from './dsItemRendererProps.js'
import { resolveDesignStudioItemSceneMetrics } from './designStudioItemScene.js'
import { getCustomSizesAllowed, isSubstrateAllowed, getItemAspectRatio, getStandardSizes, resolveResizeConstraintsFromChosenOptions, constraintFitsDimensions, artworkFitsMatWidthConstraints } from './sizingPolicy.js'

export const ASPECT_PRESETS = [
  { label: '1:1', value: 1 }, { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 }, { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 }, { label: 'Current', value: 'current' },
  { label: 'Original', value: 'original' },
]

const rounded = n => Math.round(n * 100) / 100
export function itemSizingKey(item) { return String(item?.id ?? 'single') }

export function originalAspect(item) {
  const ratio = getItemAspectRatio(item?.imagedata)
  return ratio && Math.abs(Number(item?.config_data?.artwork_rotation) || 0) % 180 === 90 ? 1 / ratio : ratio
}

export function itemDimensions(item, target = 'image') {
  const config = item?.config_data || {}
  if (target === 'image') return { width: Number(config.width), height: Number(config.height) }
  const metrics = resolveDesignStudioItemSceneMetrics({ rendererProps: buildRendererPropsFromItem(item) })
  return { width: rounded(metrics.outerWidth), height: rounded(metrics.outerHeight) }
}

export function dimensionsAtAspect({ width, height }, ratio) {
  const longest = Math.max(Number(width), Number(height))
  if (!Number.isFinite(longest) || !(longest > 0) || !(ratio > 0) || !Number.isFinite(ratio)) throw new Error('Enter a valid size and aspect ratio.')
  return ratio >= 1
    ? { width: rounded(longest), height: rounded(longest / ratio) }
    : { width: rounded(longest * ratio), height: rounded(longest) }
}

export function imageDimensionsForFinished(item, finished, rendererProps = null) {
  const image = itemDimensions(item)
  const metrics = resolveDesignStudioItemSceneMetrics({ rendererProps: rendererProps || buildRendererPropsFromItem(item) })
  const width = rounded(Number(finished.width) - (metrics.outerWidth - image.width))
  const height = rounded(Number(finished.height) - (metrics.outerHeight - image.height))
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 0.1 || height < 0.1) {
    throw new Error('The finished size cannot fit the selected frame and mats. Choose a larger size or smaller borders.')
  }
  return { width, height }
}

export function allowsItemCustomSize(item, profile) {
  return getCustomSizesAllowed({ item: item?.imagedata, profile })
}

// Availability checks are separate from frame/material limits so legacy
// restrictions can guard every edit without changing the modern sizing flow.
export function validateItemAvailability(item) {
  const substrate = item.config_data?.substrate || item.substrate
  if (!isSubstrateAllowed(item.imagedata, substrate)) throw new Error('This item is only available in select substrates.')
  if (item.imagedata?.substrate_sizes) {
    const { width, height } = itemDimensions(item)
    if (!getStandardSizes(substrate, item.imagedata.substrate_sizes).some(size => size.value.w === width && size.value.h === height)) {
      throw new Error('This artwork is only available in its listed standard sizes.')
    }
  }
}

export function configForSubstrate(item, substrate) {
  if (!isSubstrateAllowed(item.imagedata, substrate)) throw new Error('This item is only available in select substrates.')
  const config = { ...item.config_data, substrate }
  if (item.imagedata?.substrate_sizes) {
    const sizes = getStandardSizes(substrate, item.imagedata.substrate_sizes)
    const selected = sizes.find(size => size.value.w === Number(config.width) && size.value.h === Number(config.height)) || sizes[2] || sizes[0]
    if (!selected) throw new Error('No permitted sizes are available for this substrate.')
    config.width = String(selected.value.w)
    config.height = String(selected.value.h)
  }
  return config
}

export function validateItemSize(item, { profile, sizes = {}, checkEligibility = true } = {}) {
  validateItemAvailability(item)
  const { width, height } = itemDimensions(item)
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 0.1 || height < 0.1) throw new Error('Width and height must be at least 0.1 inches.')
  if (checkEligibility && !allowsItemCustomSize(item, profile)) {
    const substrate = item.config_data?.substrate || item.substrate
    const options = getStandardSizes(substrate, item.imagedata?.substrate_sizes || sizes)
    if (!options.some(option => option.value.w === width && option.value.h === height)) throw new Error('This artwork is only available in its listed standard sizes.')
  }
  const props = buildRendererPropsFromItem(item)
  const constraints = resolveResizeConstraintsFromChosenOptions(item.config_data?.chosen_options, { matWidthsArray: props.matWidthsArray })
  const failed = constraints.find(constraint => constraint.clamp && !constraintFitsDimensions(width, height, constraint))
  if (failed) throw new Error(failed.message || 'The selected frame cannot fit this size.')
  return item
}

// Dimensions are an edit intent; storage always remains image dimensions.
export function resolveSizingIntent(item, { target = 'image', width, height, ratio, aspect, swap = false, ...validation } = {}) {
  if (target === 'finished' && !allowsItemCustomSize(item, validation.profile)) throw new Error('This artwork is only available in its listed standard sizes.')
  const current = itemDimensions(item, target)
  let next = { width: width === undefined ? current.width : Number(width), height: height === undefined ? current.height : Number(height) }
  if (swap) {
    next = { width: current.height, height: current.width }
  } else if (aspect !== undefined) {
    const desired = aspect === 'original' ? originalAspect(item) : aspect === 'current' ? current.width / current.height : Number(aspect)
    next = aspect === 'current' ? current : dimensionsAtAspect(current, desired)
  } else if (ratio > 0) {
    if (width !== undefined && height === undefined) next.height = rounded(next.width / ratio)
    if (height !== undefined && width === undefined) next.width = rounded(next.height * ratio)
  }
  const image = target === 'finished' ? imageDimensionsForFinished(item, next) : next
  const config = { ...item.config_data, width: String(image.width), height: String(image.height) }
  validateItemSize({ ...item, config_data: config }, validation)
  return config
}

export function preserveFinishedSize(item, nextConfig, validation = {}) {
  const candidate = { ...item, config_data: nextConfig }
  const image = imageDimensionsForFinished(candidate, itemDimensions(item, 'finished'))
  const config = { ...nextConfig, width: String(image.width), height: String(image.height) }
  validateItemSize({ ...item, config_data: config }, validation)
  return config
}

// Probe the image area a candidate border would leave in the active sizing mode.
export function itemFitsMatWidth(item, matWidthsArray, { target = 'image', chosenOptions = item.config_data?.chosen_options || [] } = {}) {
  let dimensions = itemDimensions(item)
  if (target === 'finished') {
    try {
      const candidate = { ...item, config_data: { ...item.config_data, chosen_options: chosenOptions } }
      dimensions = imageDimensionsForFinished(item, itemDimensions(item, 'finished'), {
        ...buildRendererPropsFromItem(candidate), matWidthsArray,
      })
    } catch { return false }
  }
  return artworkFitsMatWidthConstraints(dimensions.width, dimensions.height, chosenOptions, matWidthsArray)
}
